import { Box, Typography, CircularProgress, Chip, LinearProgress } from "@mui/material";
import { ErrorOutline } from "@mui/icons-material";
import {
  Chart as ChartJS, CategoryScale, LinearScale, BarElement,
  PointElement, LineElement, ArcElement, Title, Tooltip, Legend, Filler,
} from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { Bar, Doughnut } from "react-chartjs-2";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "contexts/auth";
import api from "services/api";

import { Main, SectionTitle, SplitRow } from "v2/pages/Collaborators/CollaboratorsSummary/styled";
import { IBuyerDashboardDto, IBuyerDashboardContract, IBuyerDashboardAlert } from "./types";

ChartJS.register(CategoryScale, LinearScale, BarElement, PointElement, LineElement, ArcElement, Title, Tooltip, Legend, Filler, ChartDataLabels);

const SELLER_CNPJ = "47786631000141";

const COLORS = {
  primary: "#3300FF",
  secondary: "#F3AF3D",
  green: "#2ecc71",
  red: "#e74c3c",
  orange: "#f39c12",
  purple: "#9b59b6",
  dark: "#1E1B39",
  subtitle: "rgba(15, 15, 30, 0.6)",
};

const chartSubtitleStyle: React.CSSProperties = {
  fontFamily: "Sora",
  fontSize: "1.25rem",
  fontWeight: 600,
  lineHeight: "2rem",
  color: COLORS.subtitle,
};

const labelStyle: React.CSSProperties = {
  fontFamily: "Nunito",
  fontSize: "0.75rem",
  fontWeight: 400,
  color: COLORS.subtitle,
  lineHeight: "1.25rem",
};

const valueStyle: React.CSSProperties = {
  fontFamily: "Sora",
  fontSize: "1.5rem",
  fontWeight: 700,
  color: COLORS.dark,
  lineHeight: "2rem",
};

const tableSx = {
  width: "100%",
  borderCollapse: "collapse",
  "& th, & td": { py: 1.5, px: 2, textAlign: "left", fontSize: 14 },
  "& th": {
    fontWeight: 600,
    color: COLORS.subtitle,
    fontSize: 12,
    textTransform: "uppercase",
    fontFamily: "Nunito",
    borderBottom: "2px solid #EDEDF5",
  },
  "& td": { borderBottom: "1px solid #F5F5F5", fontFamily: "Nunito" },
};

// =============================================================================
// MAIN
// =============================================================================

export function OperationalDashboardTab() {
  const { user } = useAuth();
  const buyerCnpj = user?.organization?.cnpj;

  const { data, isLoading, isError } = useQuery({
    queryKey: ["buyerDashboard", buyerCnpj],
    queryFn: () => api.get<IBuyerDashboardDto>(`/sellers/${SELLER_CNPJ}/buyers/${buyerCnpj}/dashboard`),
    select: (res) => res.data,
    enabled: !!buyerCnpj,
  });

  if (isLoading) {
    return (
      <Main>
        <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight="400px" gap={3}>
          <CircularProgress size={30} thickness={4} />
        </Box>
      </Main>
    );
  }

  if (isError || !data) {
    return (
      <Main>
        <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight="400px" gap={3} textAlign="center">
          <ErrorOutline sx={{ fontSize: 40, color: "error.main" }} />
          <Typography variant="h6" color="textPrimary">Ops! Algo deu errado</Typography>
          <Typography variant="body1" color="textSecondary" maxWidth={400}>
            Não foi possível carregar os dados do painel operacional no momento. Por favor, tente novamente.
          </Typography>
        </Box>
      </Main>
    );
  }

  const evaluated = data.contracts.filter(c => !c.skipped);

  return (
    <>
      {/* Header */}
      <Main style={{ marginBottom: "1.5rem" }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
          <SectionTitle>{data.buyerCorporateName}</SectionTitle>
          <Typography sx={{ fontFamily: "Nunito", fontSize: "0.8rem", color: COLORS.subtitle }}>
            Gerado em {new Date(data.generatedAt).toLocaleString("pt-BR")}
          </Typography>
        </Box>
        <KpiGrid overview={data.overview} />
      </Main>

      <Main style={{ marginBottom: "1.5rem" }}>
        <SectionTitle>Qualidade da Base de Dados</SectionTitle>
        <DataQualityCharts dataQuality={data.dataQuality} />
      </Main>

      <Main style={{ marginBottom: "1.5rem" }}>
        <SectionTitle>Contratos</SectionTitle>
        <ContractsTable contracts={data.contracts} />
      </Main>

      {evaluated.map((c) => (
        <Main key={c.workspaceId} style={{ marginBottom: "1.5rem" }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <SectionTitle>{c.productName || c.productType}{c.contractNumber ? ` — Nº ${c.contractNumber}` : ""}</SectionTitle>
            <Box sx={{ display: "flex", gap: 1 }}>
              <Chip
                label={c.canOperate ? "Operável" : "Bloqueado"}
                size="small"
                sx={{ fontWeight: 600, backgroundColor: c.canOperate ? `${COLORS.green}18` : `${COLORS.red}18`, color: c.canOperate ? COLORS.green : COLORS.red }}
              />
              {!c.pipelineStatus.pipelineExecuted && (
                <Chip label="Pipeline não executado" size="small" sx={{ fontWeight: 600, backgroundColor: `${COLORS.orange}18`, color: COLORS.orange }} />
              )}
            </Box>
          </Box>

          <ContractConfigSummary c={c} />
          <FunnelSection c={c} />
          <MovementsSection c={c} />
          <PlanSection c={c} />
          <GatesSection c={c} />
        </Main>
      ))}

      <AlertsSection alerts={data.alerts} />
    </>
  );
}

// =============================================================================
// KPI GRID
// =============================================================================

function KpiGrid({ overview }: { overview: IBuyerDashboardDto["overview"] }) {
  const kpis = [
    { label: "Contratos Operáveis", value: `${overview.operableContracts}/${overview.totalContracts}` },
    { label: "Configuração Média", value: `${overview.avgConfigurationScore}/100` },
    { label: "Bloqueios", value: overview.totalBlockers },
    { label: "Alertas", value: overview.totalWarnings },
  ];

  return (
    <Box sx={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "16px" }}>
      {kpis.map((kpi) => (
        <Box key={kpi.label} sx={{ display: "flex", flexDirection: "column", padding: "16px", borderRadius: "8px", backgroundColor: "#F7F7FE", border: "1px solid #EDEDF5" }}>
          <span style={labelStyle}>{kpi.label}</span>
          <span style={valueStyle}>
            {typeof kpi.value === "number" ? kpi.value.toLocaleString("pt-BR") : kpi.value}
          </span>
        </Box>
      ))}
    </Box>
  );
}

// =============================================================================
// DATA QUALITY CHARTS
// =============================================================================

function DataQualityCharts({ dataQuality }: { dataQuality: IBuyerDashboardDto["dataQuality"] }) {
  const empFields = Object.entries(dataQuality.employees.completeness);
  const depFields = Object.entries(dataQuality.dependents.completeness);

  const totalLives = dataQuality.employees.volumeDistinct + dataQuality.dependents.volumeDistinct;

  const volumeDonutData = {
    labels: ["Funcionários", "Dependentes"],
    datasets: [{
      data: [dataQuality.employees.volumeDistinct, dataQuality.dependents.volumeDistinct],
      backgroundColor: [COLORS.primary, COLORS.secondary],
      borderWidth: 0,
    }],
  };

  const donutOptions: any = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: "70%",
    plugins: {
      legend: {
        position: "top" as const,
        labels: { boxWidth: 10, borderRadius: 10, color: COLORS.dark, font: { weight: "600", size: 16, lineHeight: 18 } },
      },
      datalabels: {
        formatter: (value: number) => Math.round((value / totalLives) * 100) + "%",
        color: "#fff",
        font: { weight: "bold", size: 14 },
      },
    },
    animation: { duration: 0 },
  };

  const completenessData = {
    labels: empFields.map(([k]) => k),
    datasets: [
      {
        label: "Funcionários",
        data: empFields.map(([, v]) => v),
        backgroundColor: COLORS.primary,
        borderColor: COLORS.primary,
        borderWidth: 1,
        borderRadius: { bottomRight: 5, topRight: 5 },
        barThickness: 14,
        maxBarThickness: 14,
      },
      ...(depFields.length > 0
        ? [{
            label: "Dependentes",
            data: empFields.map(([k]) => {
              const d = depFields.find(([dk]) => dk === k);
              return d ? d[1] : 0;
            }),
            backgroundColor: COLORS.secondary,
            borderColor: COLORS.secondary,
            borderWidth: 1,
            borderRadius: { bottomRight: 5, topRight: 5 },
            barThickness: 14,
            maxBarThickness: 14,
          }]
        : []),
    ],
  };

  const barOptions: any = {
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: "y" as const,
    layout: { padding: { right: 60, left: 10, top: 10, bottom: 10 } },
    plugins: {
      datalabels: {
        align: "end",
        anchor: "end",
        color: "#000000",
        font: { weight: "bold", size: 11 },
        formatter: (value: number) => `${value.toFixed(0)}%`,
        offset: 4,
        clip: false,
      },
      legend: {
        display: true,
        position: "top",
        align: "end",
        labels: { usePointStyle: true, color: COLORS.dark, font: { weight: "bold", size: 13, lineHeight: 18 } },
      },
    },
    scales: {
      x: {
        min: 0,
        max: 100,
        grid: { display: true, drawBorder: false, borderDash: [10, 10], color: "rgba(0, 0, 0, 0.1)" },
        ticks: { color: COLORS.dark, callback: (v: any) => `${v}%`, font: { size: 11 } },
      },
      y: {
        grid: { display: false },
        ticks: { color: COLORS.dark, font: { size: 12 }, padding: 8 },
        afterFit: (scale: any) => { scale.width = Math.max(scale.width, 120); },
      },
    },
    animation: { duration: 0 },
  };

  const hasHealthWarnings =
    dataQuality.staleness.recordsNotUpdatedIn90Days > 0 ||
    dataQuality.conflicts.cpfsWithMultipleNames > 0;

  return (
    <>
      {/* Volume stats */}
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "16px", mb: 2 }}>
        <Box sx={{ display: "flex", flexDirection: "column", padding: "12px 16px", borderRadius: "8px", backgroundColor: "#F7F7FE", border: "1px solid #EDEDF5" }}>
          <span style={labelStyle}>Funcionários (distintos)</span>
          <span style={{ ...valueStyle, fontSize: "1.25rem" }}>{dataQuality.employees.volumeDistinct.toLocaleString("pt-BR")}</span>
        </Box>
        <Box sx={{ display: "flex", flexDirection: "column", padding: "12px 16px", borderRadius: "8px", backgroundColor: "#F7F7FE", border: "1px solid #EDEDF5" }}>
          <span style={labelStyle}>Dependentes (distintos)</span>
          <span style={{ ...valueStyle, fontSize: "1.25rem" }}>{dataQuality.dependents.volumeDistinct.toLocaleString("pt-BR")}</span>
        </Box>
        <Box sx={{ display: "flex", flexDirection: "column", padding: "12px 16px", borderRadius: "8px", backgroundColor: "#F7F7FE", border: "1px solid #EDEDF5" }}>
          <span style={labelStyle}>Duplicatas Funcionários</span>
          <span style={{ ...valueStyle, fontSize: "1.25rem", color: dataQuality.employees.duplicateRate > 10 ? COLORS.red : COLORS.dark }}>
            {dataQuality.employees.duplicateRate.toFixed(1)}%
          </span>
          <span style={{ ...labelStyle, fontSize: "0.7rem" }}>{dataQuality.employees.volumeRaw.toLocaleString("pt-BR")} registros brutos</span>
        </Box>
        <Box sx={{ display: "flex", flexDirection: "column", padding: "12px 16px", borderRadius: "8px", backgroundColor: "#F7F7FE", border: "1px solid #EDEDF5" }}>
          <span style={labelStyle}>Duplicatas Dependentes</span>
          <span style={{ ...valueStyle, fontSize: "1.25rem", color: dataQuality.dependents.duplicateRate > 10 ? COLORS.red : COLORS.dark }}>
            {dataQuality.dependents.duplicateRate.toFixed(1)}%
          </span>
          <span style={{ ...labelStyle, fontSize: "0.7rem" }}>{dataQuality.dependents.volumeRaw.toLocaleString("pt-BR")} registros brutos</span>
        </Box>
      </Box>

      <SplitRow>
        <div style={{ flex: 1 }}>
          <span style={chartSubtitleStyle}>Distribuição de Vidas</span>
          <Box sx={{ padding: "1rem", height: 300, position: "relative", display: "flex", justifyContent: "center", alignItems: "center" }}>
            <Box sx={{ width: "80%", height: "100%", position: "relative" }}>
              <Doughnut data={volumeDonutData} options={donutOptions} />
              <Box sx={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", textAlign: "center" }}>
                <Typography variant="h6" sx={{ color: COLORS.dark, fontWeight: "bold" }}>
                  {totalLives.toLocaleString("pt-BR")}
                </Typography>
                <Typography variant="body2" sx={{ color: COLORS.dark }}>vidas distintas</Typography>
              </Box>
            </Box>
          </Box>
        </div>
        <div style={{ flex: 2 }}>
          <span style={chartSubtitleStyle}>Completude por Campo</span>
          <Box sx={{ padding: "1rem", height: Math.max(350, empFields.length * 55), position: "relative" }}>
            <Bar data={completenessData} options={barOptions} />
          </Box>
        </div>
      </SplitRow>

      {hasHealthWarnings && (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}>
          {dataQuality.staleness.recordsNotUpdatedIn90Days > 0 && (
            <Chip label={`${dataQuality.staleness.recordsNotUpdatedIn90Days} registros > 90 dias sem atualização`} color="warning" variant="outlined" size="small" />
          )}
          {dataQuality.staleness.recordsNotUpdatedIn180Days > 0 && (
            <Chip label={`${dataQuality.staleness.recordsNotUpdatedIn180Days} registros > 180 dias sem atualização`} color="error" variant="outlined" size="small" />
          )}
          {dataQuality.conflicts.cpfsWithMultipleNames > 0 && (
            <Chip label={`${dataQuality.conflicts.cpfsWithMultipleNames} nomes conflitantes entre fontes`} color="warning" variant="outlined" size="small" />
          )}
        </Box>
      )}

      {/* Campaign Suggestions */}
      {dataQuality.campaignSuggestions.length > 0 && (
        <>
          <span style={{ ...chartSubtitleStyle, marginTop: "16px", display: "block" }}>Sugestões de Campanha</span>
          <Box sx={{ overflowX: "auto" }}>
            <Box component="table" sx={tableSx}>
              <thead>
                <tr>
                  <th>Campo</th>
                  <th>Registros Faltantes</th>
                  <th>Impacto</th>
                  <th>Esforço</th>
                </tr>
              </thead>
              <tbody>
                {dataQuality.campaignSuggestions.map((s, i) => (
                  <tr key={i}>
                    <td><Typography variant="body2" fontWeight={600} sx={{ color: COLORS.dark }}>{s.fieldName}</Typography></td>
                    <td>{s.gapCount.toLocaleString("pt-BR")}</td>
                    <td>{s.impactIfFilled}</td>
                    <td>
                      <Chip
                        label={s.effort}
                        size="small"
                        sx={{
                          fontWeight: 600,
                          backgroundColor: s.effort === "LOW" ? `${COLORS.green}18` : s.effort === "MEDIUM" ? `${COLORS.orange}18` : `${COLORS.red}18`,
                          color: s.effort === "LOW" ? COLORS.green : s.effort === "MEDIUM" ? COLORS.orange : COLORS.red,
                          border: "none",
                        }}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </Box>
          </Box>
        </>
      )}
    </>
  );
}

// =============================================================================
// CONTRACTS TABLE
// =============================================================================

function ContractsTable({ contracts }: { contracts: IBuyerDashboardContract[] }) {
  return (
    <Box sx={{ overflowX: "auto" }}>
      <Box component="table" sx={tableSx}>
        <thead>
          <tr>
            <th>Produto</th>
            <th>Nº Contrato</th>
            <th>Tipo</th>
            <th>Inclusão</th>
            <th>Exclusão</th>
            <th>Política</th>
            <th>Planos</th>
            <th>Atividades</th>
            <th>Score</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {contracts.map((c) => (
            <tr key={c.workspaceId}>
              <td>
                <Typography variant="body2" fontWeight={600} sx={{ color: COLORS.dark }}>
                  {c.productName || c.productType}
                </Typography>
              </td>
              <td>{c.contractNumber || "—"}</td>
              <td>{c.contractType || "—"}</td>
              <td>{c.configuration.inclusionRule || "—"}</td>
              <td>{c.configuration.exclusionRule || "—"}</td>
              <td>{c.configuration.benefitsPolicyType || "—"}</td>
              <td>{c.configuration.plansDefinedCount}</td>
              <td>{c.configuration.activitiesLinkedCount}</td>
              <td>
                <Typography variant="body2" fontWeight={700} sx={{ color: scoreColor(c.configuration.overallScore) }}>
                  {c.configuration.overallScore}
                </Typography>
              </td>
              <td>
                <Chip
                  label={c.skipped ? "N/A" : c.canOperate ? "Operável" : "Bloqueado"}
                  size="small"
                  sx={{
                    fontWeight: 600,
                    backgroundColor: c.skipped ? "#F5F5F5" : c.canOperate ? `${COLORS.green}18` : `${COLORS.red}18`,
                    color: c.skipped ? COLORS.subtitle : c.canOperate ? COLORS.green : COLORS.red,
                    border: "none",
                  }}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </Box>
    </Box>
  );
}

// =============================================================================
// CONTRACT CONFIG SUMMARY (inline KPIs per contract)
// =============================================================================

function ContractConfigSummary({ c }: { c: IBuyerDashboardContract }) {
  const cfg = c.configuration;
  const items = [
    { label: "Completude", value: cfg.completenessScore },
    { label: "Coerência", value: cfg.coherenceScore },
    { label: "Score Geral", value: cfg.overallScore },
    { label: "Planos", value: cfg.plansDefinedCount },
    { label: "Atividades", value: cfg.activitiesLinkedCount },
  ];

  return (
    <Box sx={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
      {items.map((item) => (
        <Box key={item.label} sx={{ display: "flex", flexDirection: "column", padding: "8px 14px", borderRadius: "8px", backgroundColor: "#F7F7FE", border: "1px solid #EDEDF5", minWidth: 100 }}>
          <span style={{ ...labelStyle, fontSize: "0.7rem" }}>{item.label}</span>
          <span style={{
            fontFamily: "Sora",
            fontSize: "1.1rem",
            fontWeight: 700,
            color: typeof item.value === "number" && item.label.includes("Score") || item.label === "Completude" || item.label === "Coerência"
              ? scoreColor(item.value as number)
              : COLORS.dark,
            lineHeight: "1.5rem",
          }}>
            {item.value}
          </span>
        </Box>
      ))}
      {cfg.inclusionRule && (
        <Box sx={{ display: "flex", flexDirection: "column", padding: "8px 14px", borderRadius: "8px", backgroundColor: "#F7F7FE", border: "1px solid #EDEDF5" }}>
          <span style={{ ...labelStyle, fontSize: "0.7rem" }}>Inclusão</span>
          <span style={{ fontFamily: "Nunito", fontSize: "0.85rem", fontWeight: 600, color: COLORS.dark }}>{cfg.inclusionRule}</span>
        </Box>
      )}
      {cfg.exclusionRule && (
        <Box sx={{ display: "flex", flexDirection: "column", padding: "8px 14px", borderRadius: "8px", backgroundColor: "#F7F7FE", border: "1px solid #EDEDF5" }}>
          <span style={{ ...labelStyle, fontSize: "0.7rem" }}>Exclusão</span>
          <span style={{ fontFamily: "Nunito", fontSize: "0.85rem", fontWeight: 600, color: COLORS.dark }}>{cfg.exclusionRule}</span>
        </Box>
      )}
      {cfg.benefitsPolicyType && (
        <Box sx={{ display: "flex", flexDirection: "column", padding: "8px 14px", borderRadius: "8px", backgroundColor: "#F7F7FE", border: "1px solid #EDEDF5" }}>
          <span style={{ ...labelStyle, fontSize: "0.7rem" }}>Política</span>
          <span style={{ fontFamily: "Nunito", fontSize: "0.85rem", fontWeight: 600, color: COLORS.dark }}>{cfg.benefitsPolicyType}</span>
        </Box>
      )}
    </Box>
  );
}

// =============================================================================
// FUNNEL
// =============================================================================

function FunnelSection({ c }: { c: IBuyerDashboardContract }) {
  const steps = [
    { label: "Endereçáveis", value: c.funnel.addressable },
    { label: "Elegíveis", value: c.funnel.eligible },
    { label: "Movimentados", value: c.funnel.movements },
  ];

  const funnelBarData = {
    labels: steps.map(s => s.label),
    datasets: [{
      label: "Beneficiários",
      data: steps.map(s => s.value),
      backgroundColor: COLORS.primary,
      borderColor: COLORS.primary,
      borderWidth: 1,
      borderRadius: { bottomRight: 5, topRight: 5 },
      barThickness: 28,
      maxBarThickness: 28,
    }],
  };

  const funnelOptions: any = {
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: "y" as const,
    layout: { padding: { right: 80, left: 10, top: 10, bottom: 10 } },
    plugins: {
      datalabels: {
        align: "end",
        anchor: "end",
        color: "#000000",
        font: { weight: "bold", size: 14 },
        formatter: (value: number) => value.toLocaleString("pt-BR"),
        offset: 4,
        clip: false,
      },
      legend: { display: false },
    },
    scales: {
      x: {
        grid: { display: true, drawBorder: false, borderDash: [10, 10], color: "rgba(0, 0, 0, 0.1)" },
        ticks: { color: COLORS.dark },
      },
      y: { grid: { display: false }, ticks: { color: COLORS.dark, font: { size: 14 } } },
    },
    animation: { duration: 0 },
  };

  return (
    <>
      <span style={chartSubtitleStyle}>Funil de Beneficiários</span>
      <Box sx={{ padding: "1rem", height: 180, position: "relative" }}>
        <Bar data={funnelBarData} options={funnelOptions} />
      </Box>
      <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
        <Typography variant="body2" sx={{ color: COLORS.subtitle, fontFamily: "Nunito" }}>
          Conversão total: <strong style={{ color: COLORS.dark }}>{c.funnel.conversionRate.toFixed(1)}%</strong>
        </Typography>
        <Typography variant="body2" sx={{ color: COLORS.subtitle, fontFamily: "Nunito" }}>
          Pendentes: <strong style={{ color: c.funnel.pending > 0 ? COLORS.orange : COLORS.green }}>{c.funnel.pending.toLocaleString("pt-BR")}</strong>
        </Typography>
        {c.funnel.gapAddressableToEligible > 0 && (
          <Typography variant="body2" sx={{ color: COLORS.subtitle, fontFamily: "Nunito" }}>
            Gap endereçável → elegível: <strong style={{ color: c.funnel.gapAddressableToEligible > 30 ? COLORS.red : COLORS.orange }}>{c.funnel.gapAddressableToEligible.toFixed(0)}%</strong>
          </Typography>
        )}
        {c.funnel.gapEligibleToMovements > 0 && (
          <Typography variant="body2" sx={{ color: COLORS.subtitle, fontFamily: "Nunito" }}>
            Gap elegível → movimentado: <strong style={{ color: c.funnel.gapEligibleToMovements > 30 ? COLORS.red : COLORS.orange }}>{c.funnel.gapEligibleToMovements.toFixed(0)}%</strong>
          </Typography>
        )}
      </Box>
    </>
  );
}

// =============================================================================
// MOVEMENTS
// =============================================================================

function MovementsSection({ c }: { c: IBuyerDashboardContract }) {
  const total = c.currentCycle.inclusionsDetected + c.currentCycle.exclusionsDetected + c.currentCycle.changesDetected;

  const donutData = {
    labels: ["Inclusões", "Exclusões", "Alterações"],
    datasets: [{
      data: [c.currentCycle.inclusionsDetected, c.currentCycle.exclusionsDetected, c.currentCycle.changesDetected],
      backgroundColor: [COLORS.green, COLORS.red, COLORS.primary],
      borderWidth: 0,
    }],
  };

  const donutOptions: any = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: "70%",
    plugins: {
      legend: {
        position: "top" as const,
        labels: { boxWidth: 10, borderRadius: 10, color: COLORS.dark, font: { weight: "600", size: 14, lineHeight: 18 } },
      },
      datalabels: {
        formatter: (value: number) => {
          if (total === 0) return "0%";
          return Math.round((value / total) * 100) + "%";
        },
        color: "#fff",
        font: { weight: "bold", size: 12 },
        display: (ctx: any) => ctx.dataset.data[ctx.dataIndex] > 0,
      },
    },
    animation: { duration: 0 },
  };

  const anomalies = [
    c.currentCycle.inclusionsWithoutHiringDate > 0 && `${c.currentCycle.inclusionsWithoutHiringDate} inclusões sem data de admissão`,
    c.currentCycle.exclusionsWithoutDismissalDate > 0 && `${c.currentCycle.exclusionsWithoutDismissalDate} exclusões sem data de demissão`,
    c.currentCycle.changesPhantomCount > 0 && `${c.currentCycle.changesPhantomCount} alterações fantasma`,
    c.currentCycle.delayedStaleCount > 0 && `${c.currentCycle.delayedStaleCount} movimentações travadas`,
  ].filter(Boolean) as string[];

  return (
    <>
      <span style={chartSubtitleStyle}>Movimentações do Ciclo</span>
      <SplitRow>
        <div style={{ flex: 1 }}>
          <Box sx={{ padding: "1rem", height: 260, position: "relative", display: "flex", justifyContent: "center", alignItems: "center" }}>
            <Box sx={{ width: "80%", height: "100%", position: "relative" }}>
              <Doughnut data={donutData} options={donutOptions} />
              <Box sx={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", textAlign: "center" }}>
                <Typography variant="h6" sx={{ color: COLORS.dark, fontWeight: "bold" }}>{total}</Typography>
                <Typography variant="body2" sx={{ color: COLORS.dark }}>total</Typography>
              </Box>
            </Box>
          </Box>
        </div>
        <div style={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "center", gap: "12px" }}>
          {/* Vigency rates */}
          <Box sx={{ display: "flex", gap: "12px" }}>
            <Box sx={{ flex: 1, padding: "10px 14px", borderRadius: "8px", backgroundColor: "#F7F7FE", border: "1px solid #EDEDF5" }}>
              <span style={{ ...labelStyle, fontSize: "0.7rem" }}>Vigência Inclusão Correta</span>
              <Typography sx={{ fontFamily: "Sora", fontSize: "1.1rem", fontWeight: 700, color: scoreColor(c.currentCycle.inclusionVigencyCorrectRate) }}>
                {c.currentCycle.inclusionVigencyCorrectRate.toFixed(1)}%
              </Typography>
            </Box>
            <Box sx={{ flex: 1, padding: "10px 14px", borderRadius: "8px", backgroundColor: "#F7F7FE", border: "1px solid #EDEDF5" }}>
              <span style={{ ...labelStyle, fontSize: "0.7rem" }}>Vigência Exclusão Correta</span>
              <Typography sx={{ fontFamily: "Sora", fontSize: "1.1rem", fontWeight: 700, color: scoreColor(c.currentCycle.exclusionVigencyCorrectRate) }}>
                {c.currentCycle.exclusionVigencyCorrectRate.toFixed(1)}%
              </Typography>
            </Box>
          </Box>

          {anomalies.length > 0 && (
            <Box sx={{ p: 2, borderRadius: "8px", backgroundColor: "#FFF8E1", border: `1px solid ${COLORS.orange}40` }}>
              {anomalies.map((a, i) => (
                <Typography key={i} variant="body2" sx={{ fontSize: 13, fontFamily: "Nunito", color: COLORS.dark, mb: i < anomalies.length - 1 ? 0.5 : 0 }}>
                  ⚠ {a}
                </Typography>
              ))}
            </Box>
          )}
        </div>
      </SplitRow>
    </>
  );
}

// =============================================================================
// PLAN ASSIGNMENT
// =============================================================================

function PlanSection({ c }: { c: IBuyerDashboardContract }) {
  const withPlan = 100 - c.planAssignment.withoutPlanRate;
  const hasIssue = c.planAssignment.unmappedPositionsCount > 0 && !c.planAssignment.withoutPlanIsExpected;

  return (
    <Box sx={{ display: "flex", flexDirection: "column" }}>
      <span style={chartSubtitleStyle}>Atribuição de Planos</span>
      <Box sx={{ mt: 2 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
          <Typography variant="body2" sx={{ fontFamily: "Nunito", color: COLORS.dark }}>Com plano atribuído</Typography>
          <Typography variant="body2" sx={{ fontFamily: "Nunito", fontWeight: 700, color: COLORS.dark }}>
            {withPlan.toFixed(1)}%
            {c.planAssignment.withoutPlanIsExpected && c.planAssignment.withoutPlanRate === 100 && (
              <span style={{ fontWeight: 400, color: COLORS.subtitle, marginLeft: 8, fontSize: 12 }}>(esperado)</span>
            )}
          </Typography>
        </Box>
        <LinearProgress
          variant="determinate"
          value={withPlan}
          sx={{
            height: 12,
            borderRadius: 6,
            backgroundColor: "#EDEDF5",
            "& .MuiLinearProgress-bar": {
              backgroundColor: hasIssue ? COLORS.orange : COLORS.primary,
              borderRadius: 6,
            },
          }}
        />
        <Typography variant="caption" sx={{ mt: 1, display: "block", fontFamily: "Nunito", color: COLORS.subtitle }}>
          {c.planAssignment.contractType} · {c.planAssignment.policyType}
        </Typography>
        {hasIssue && (
          <Box sx={{ mt: 1.5, p: 1.5, borderRadius: "8px", backgroundColor: "#FFF8E1", border: `1px solid ${COLORS.orange}40` }}>
            <Typography variant="body2" sx={{ fontSize: 13, fontFamily: "Nunito", color: COLORS.dark }}>
              ⚠ {c.planAssignment.unmappedPositionsCount} sem plano — Cargos: <strong>{c.planAssignment.unmappedPositions.join(", ")}</strong>
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
}

// =============================================================================
// GATES (per contract)
// =============================================================================

function GatesSection({ c }: { c: IBuyerDashboardContract }) {
  const gates = c.configuration.gates;
  const contradictions = c.configuration.contradictions;

  if (gates.length === 0 && contradictions.length === 0) return null;

  const blockers = gates.filter(g => g.severity === "BLOCKER");
  const majors = gates.filter(g => g.severity === "MAJOR");
  const others = gates.filter(g => g.severity !== "BLOCKER" && g.severity !== "MAJOR");

  return (
    <>
      <span style={chartSubtitleStyle}>Verificações de Configuração</span>

      {blockers.length > 0 && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
          {blockers.map((g, i) => (
            <Box key={i} sx={{ p: 1.5, borderRadius: "8px", backgroundColor: "#FFF3F3", border: `1px solid ${COLORS.red}30` }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                <Chip label="BLOQUEIO" size="small" sx={{ fontWeight: 700, fontSize: 10, backgroundColor: COLORS.red, color: "#fff", height: 20 }} />
                <Typography variant="body2" sx={{ fontWeight: 600, fontFamily: "Nunito", color: COLORS.dark }}>{g.testName}</Typography>
              </Box>
              <Typography variant="caption" sx={{ fontFamily: "Nunito", color: COLORS.subtitle, display: "block" }}>{g.message}</Typography>
              <Typography variant="caption" sx={{ fontFamily: "Nunito", color: COLORS.dark, display: "block" }}>→ {g.action}</Typography>
            </Box>
          ))}
        </Box>
      )}

      {majors.length > 0 && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: "8px", mt: blockers.length > 0 ? 1 : 0 }}>
          {majors.map((g, i) => (
            <Box key={i} sx={{ p: 1.5, borderRadius: "8px", backgroundColor: "#FFF8E1", border: `1px solid ${COLORS.orange}40` }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                <Chip label="ATENÇÃO" size="small" sx={{ fontWeight: 700, fontSize: 10, backgroundColor: COLORS.orange, color: "#fff", height: 20 }} />
                <Typography variant="body2" sx={{ fontWeight: 600, fontFamily: "Nunito", color: COLORS.dark }}>{g.testName}</Typography>
              </Box>
              <Typography variant="caption" sx={{ fontFamily: "Nunito", color: COLORS.subtitle, display: "block" }}>{g.message}</Typography>
              <Typography variant="caption" sx={{ fontFamily: "Nunito", color: COLORS.dark, display: "block" }}>→ {g.action}</Typography>
            </Box>
          ))}
        </Box>
      )}

      {others.length > 0 && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: "8px", mt: (blockers.length > 0 || majors.length > 0) ? 1 : 0 }}>
          {others.map((g, i) => (
            <Box key={i} sx={{ p: 1.5, borderRadius: "8px", backgroundColor: "#F7F7FE", border: "1px solid #EDEDF5" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                <Chip label={g.severity} size="small" sx={{ fontWeight: 700, fontSize: 10, backgroundColor: COLORS.primary, color: "#fff", height: 20 }} />
                <Typography variant="body2" sx={{ fontWeight: 600, fontFamily: "Nunito", color: COLORS.dark }}>{g.testName}</Typography>
              </Box>
              <Typography variant="caption" sx={{ fontFamily: "Nunito", color: COLORS.subtitle, display: "block" }}>{g.message}</Typography>
              <Typography variant="caption" sx={{ fontFamily: "Nunito", color: COLORS.dark, display: "block" }}>→ {g.action}</Typography>
            </Box>
          ))}
        </Box>
      )}

      {contradictions.length > 0 && (
        <Box sx={{ mt: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, fontFamily: "Nunito", color: COLORS.dark, mb: 0.5 }}>Contradições</Typography>
          {contradictions.map((c, i) => (
            <Typography key={i} variant="caption" sx={{ fontFamily: "Nunito", color: COLORS.subtitle, display: "block" }}>
              • {c}
            </Typography>
          ))}
        </Box>
      )}
    </>
  );
}

// =============================================================================
// ALERTS
// =============================================================================

function AlertsSection({ alerts }: { alerts: IBuyerDashboardDto["alerts"] }) {
  if (alerts.critical.length === 0 && alerts.warnings.length === 0) return null;

  return (
    <Main>
      <SectionTitle>Alertas</SectionTitle>
      {alerts.critical.length > 0 && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
          {alerts.critical.map((a, i) => (
            <AlertRow key={i} alert={a} severity="critical" />
          ))}
        </Box>
      )}
      {alerts.warnings.length > 0 && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: "8px", mt: alerts.critical.length > 0 ? 2 : 0 }}>
          {alerts.warnings.map((a, i) => (
            <AlertRow key={i} alert={a} severity="warning" />
          ))}
        </Box>
      )}
    </Main>
  );
}

function AlertRow({ alert, severity }: { alert: IBuyerDashboardAlert; severity: "critical" | "warning" }) {
  const isCritical = severity === "critical";
  return (
    <Box sx={{ p: 2, borderRadius: "8px", backgroundColor: isCritical ? "#FFF3F3" : "#FFF8E1", border: `1px solid ${isCritical ? COLORS.red : COLORS.orange}30` }}>
      <Typography variant="body2" sx={{ fontWeight: 600, fontFamily: "Nunito", color: COLORS.dark }}>
        {alert.contract} — {alert.problem}
      </Typography>
      <Typography variant="caption" sx={{ fontFamily: "Nunito", color: COLORS.subtitle }}>
        → {alert.action}
      </Typography>
    </Box>
  );
}

// =============================================================================
// HELPERS
// =============================================================================

function scoreColor(v: number) {
  return v >= 80 ? COLORS.green : v >= 50 ? COLORS.orange : COLORS.red;
}
